import React from "react";
import dayjs, { Dayjs } from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import WorkDay from "../../Types/WorkDay";
import WorkPeriod from "../../Types/WorkPeriod";
import WorkDayRepository from "../../Repositories/WorkDayRepository";
import WorkPeriodEditorDialog from "./WorkPeriodEditorDialog";
import WorkDayService from "../../Services/WorkDayService";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import { CountedState } from "../../Types/Enums/CountedState";

interface Props {
    workDay: WorkDay | Dayjs;
    selectedPeriodId: string | null;
    onSelectedPeriodChange: (periodId: string | null) => void;
    onUpdate: (updated: WorkDay) => void;
}

interface State {
    editorMode: "create" | "edit" | null;
    editorPeriod: WorkPeriod | null;
    deletingPeriodId: string | null;
}

const ACCENT = "#c79656";
const TEXT = "#0f172a";
const TEXT_MUTED = "rgba(0,0,0,0.55)";
const BORDER = "rgba(0,0,0,0.08)";

export default class WorkDayTipOverview extends React.Component<Props, State> {
    static contextType = AuthContext;
    context!: AuthContextType;

    constructor(props: Props) {
        super(props);
        this.state = {
            editorMode: null,
            editorPeriod: null,
            deletingPeriodId: null,
        };
    }

    private fmt(n: number) {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);
    }

    private normalizeWorkDay() {
        if (dayjs.isDayjs(this.props.workDay)) {
            return new WorkDay("", this.props.workDay.format("YYYY-MM-DD"), 0, 0, false, CountedState.NotCounted, [], []);
        }

        return WorkDayService.buildWorkDay(this.props.workDay, WorkDayService.getDisplayPeriods(this.props.workDay));
    }

    private getDisplayPeriods() {
        if (dayjs.isDayjs(this.props.workDay)) return [];
        return WorkDayService.getDisplayPeriods(this.props.workDay);
    }

    private async persistWorkPeriod(workPeriod: WorkPeriod) {
        const source = this.props.workDay;
        const periods = this.getDisplayPeriods();
        const isExistingPeriod = !dayjs.isDayjs(source) && periods.some((period) => period.Id === workPeriod.Id);

        if (dayjs.isDayjs(source)) {
            const updated = WorkDayService.buildWorkDay(source, [workPeriod]);
            const result = await WorkDayRepository.addWorkDay(this.context.locationId, updated);
            if (result.isFailed() || !result.result?.id) return;
            updated.Id = result.result.id;

            const reloaded = await WorkDayRepository.getWorkDayByDate(this.context.locationId, dayjs(updated.Date));
            const hydrated = reloaded.result ?? updated;
            const hydratedPeriodId = WorkDayService.getDisplayPeriods(hydrated)[0]?.Id ?? null;

            this.setState({ editorMode: null, editorPeriod: null });
            this.props.onSelectedPeriodChange(hydratedPeriodId);
            this.props.onUpdate(hydrated);
            return;
        }

        if (isExistingPeriod) {
            const result = await WorkDayRepository.updateWorkPeriod(this.context.locationId, source.Id, workPeriod);
            if (result.isFailed()) return;

            const updated = WorkDayService.buildWorkDay(
                source,
                periods.map((period) => period.Id === workPeriod.Id ? workPeriod : period),
            );
            this.setState({ editorMode: null, editorPeriod: null });
            this.props.onSelectedPeriodChange(workPeriod.Id);
            this.props.onUpdate(updated);
        } else {
            const result = await WorkDayRepository.addWorkPeriod(this.context.locationId, source.Id, workPeriod);
            if (result.isFailed()) return;

            const createdPeriod = WorkDayService.cloneWorkPeriod(workPeriod);
            createdPeriod.Id = result.result?.id ?? createdPeriod.Id;
            const updated = WorkDayService.buildWorkDay(source, [...periods, createdPeriod]);
            this.setState({ editorMode: null, editorPeriod: null });
            this.props.onSelectedPeriodChange(createdPeriod.Id);
            this.props.onUpdate(updated);
        }
    }

    private openNewPeriod = () => {
        const period = WorkDayService.createEmptyWorkPeriod(this.normalizeWorkDay().Date);
        period.Name = "Avond";

        this.setState({
            editorMode: "create",
            editorPeriod: period,
        });
    };

    private openEditPeriod(period: WorkPeriod) {
        this.setState({
            editorMode: "edit",
            editorPeriod: WorkDayService.cloneWorkPeriod(period),
        });
    }

    private async deleteWorkPeriod(period: WorkPeriod) {
        const source = this.props.workDay;
        if (dayjs.isDayjs(source)) return;

        this.setState({ deletingPeriodId: period.Id });

        const result = await WorkDayRepository.deleteWorkPeriod(this.context.locationId, source.Id, period.Id);
        if (result.isFailed()) {
            this.setState({ deletingPeriodId: null });
            return;
        }

        const remainingPeriods = this.getDisplayPeriods().filter((item) => item.Id !== period.Id);
        const updated = WorkDayService.buildWorkDay(source, remainingPeriods);
        const nextSelectedPeriodId = this.props.selectedPeriodId === period.Id
            ? remainingPeriods[0]?.Id ?? null
            : this.props.selectedPeriodId;

        this.setState((prev) => ({
            deletingPeriodId: null,
            editorMode: prev.editorPeriod?.Id === period.Id ? null : prev.editorMode,
            editorPeriod: prev.editorPeriod?.Id === period.Id ? null : prev.editorPeriod,
        }));
        this.props.onSelectedPeriodChange(nextSelectedPeriodId);
        this.props.onUpdate(updated);
    }

    public render() {
        const { editorMode, editorPeriod, deletingPeriodId } = this.state;
        const normalizedWorkDay = this.normalizeWorkDay();
        const periods = this.getDisplayPeriods();

        return (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 4, height: 20, borderRadius: 4, background: ACCENT }} />
                        <span style={{ fontSize: 16, fontWeight: 800, color: TEXT }}>Work periods</span>
                        <button
                            onClick={async () => {
                                const source = this.props.workDay;
                                const normalized = this.normalizeWorkDay();
                                const nextClosed = !normalized.IsClosed;
                                const updated = new WorkDay(
                                    dayjs.isDayjs(source) ? "" : normalized.Id,
                                    normalized.Date,
                                    0,
                                    0,
                                    nextClosed,
                                    normalized.CountedState,
                                    [...normalized.CountedBy],
                                    [],
                                    [],
                                );

                                if (dayjs.isDayjs(source)) {
                                    if (!nextClosed) return;

                                const result = await WorkDayRepository.addWorkDay(this.context.locationId, updated);
                                if (result.isFailed() || !result.result?.id) return;
                                updated.Id = result.result.id;

                                const reloaded = await WorkDayRepository.getWorkDayByDate(this.context.locationId, dayjs(updated.Date));
                                const hydrated = reloaded.result ?? updated;
                                this.props.onSelectedPeriodChange(null);
                                this.props.onUpdate(hydrated);
                                return;
                            } else {
                                const result = await WorkDayRepository.updateWorkDay(this.context.locationId, updated);
                                if (result.isFailed()) return;
                                }

                                this.props.onSelectedPeriodChange(null);
                                this.props.onUpdate(updated);
                            }}
                            style={{
                                padding: "6px 10px",
                                borderRadius: 999,
                                border: `1px solid ${normalizedWorkDay.IsClosed ? ACCENT : BORDER}`,
                                background: normalizedWorkDay.IsClosed ? `${ACCENT}18` : "#fff",
                                color: normalizedWorkDay.IsClosed ? "#7c5a31" : TEXT_MUTED,
                                fontWeight: 700,
                                fontSize: 12,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <span>Closed</span>
                            <span
                                style={{
                                    width: 32,
                                    height: 18,
                                    borderRadius: 999,
                                    background: normalizedWorkDay.IsClosed ? ACCENT : "rgba(0,0,0,0.14)",
                                    padding: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: normalizedWorkDay.IsClosed ? "flex-end" : "flex-start",
                                    boxSizing: "border-box",
                                }}
                            >
                                <span
                                    style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        background: "#fff",
                                        boxShadow: "0 1px 4px rgba(0,0,0,0.20)",
                                        display: "block",
                                    }}
                                />
                            </span>
                        </button>
                    </div>

                    <button
                        onClick={this.openNewPeriod}
                        style={{
                            padding: "6px 12px",
                            borderRadius: 8,
                            border: "none",
                            background: ACCENT,
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 12,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                        }}
                    >
                        <AddIcon sx={{ fontSize: 14, color: "#fff" }} />
                        New
                    </button>
                </div>

                <div
                    style={{
                        border: `1px solid ${ACCENT}22`,
                        borderRadius: 12,
                        padding: "10px 12px",
                        background: `linear-gradient(180deg, ${ACCENT}10 0%, rgba(255,255,255,0.96) 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        flexShrink: 0,
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: TEXT_MUTED }}>
                            Day total
                        </span>
                        <span style={{ fontSize: 18, fontWeight: 800, color: TEXT, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
                            {this.fmt(normalizedWorkDay.TipCash + normalizedWorkDay.TipPin)}
                        </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, color: TEXT_MUTED, fontSize: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <span>Cash {this.fmt(normalizedWorkDay.TipCash)}</span>
                        <span>PIN {this.fmt(normalizedWorkDay.TipPin)}</span>
                    </div>
                </div>

                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        border: `1px solid ${BORDER}`,
                        borderRadius: 12,
                        background: "#fff",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                        {periods.length === 0 ? (
                            <div
                                style={{
                                    flex: 1,
                                    minHeight: 180,
                                    border: `1px dashed ${BORDER}`,
                                    borderRadius: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 16,
                                    textAlign: "center",
                                    color: TEXT_MUTED,
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                }}
                            >
                                No work periods for this day. Add one to assign workers and tips to a specific period.
                            </div>
                        ) : (
                            periods.map((period, index) => {
                                const isSelected = this.props.selectedPeriodId === period.Id;
                                const total = (period.TipCash ?? 0) + (period.TipPin ?? 0);

                                return (
                                    <button
                                        key={period.Id}
                                        onClick={() => this.props.onSelectedPeriodChange(period.Id)}
                                        style={{
                                            border: isSelected ? `1px solid ${ACCENT}` : `1px solid ${BORDER}`,
                                            borderRadius: 12,
                                            padding: "10px 12px",
                                            background: isSelected ? `${ACCENT}10` : "#fff",
                                            textAlign: "left",
                                            cursor: "pointer",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 8,
                                        }}
                                        >
                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                        <span style={{ fontSize: 13, fontWeight: 700, color: TEXT, whiteSpace: "nowrap" }}>
                                                            {WorkDayService.getWorkPeriodLabel(period, index)}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                                        <span style={{ fontSize: 15, fontWeight: 800, color: TEXT, fontVariantNumeric: "tabular-nums" }}>{this.fmt(total)}</span>
                                                        <button
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                this.openEditPeriod(period);
                                                            }}
                                                            aria-label={`Edit ${WorkDayService.getWorkPeriodLabel(period, index)}`}
                                                            style={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: 8,
                                                                border: `1px solid ${BORDER}`,
                                                                background: "#fff",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                color: "#7c5a31",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                                        </button>
                                                        <button
                                                            onClick={async (event) => {
                                                                event.stopPropagation();
                                                                await this.deleteWorkPeriod(period);
                                                            }}
                                                            aria-label={`Delete ${WorkDayService.getWorkPeriodLabel(period, index)}`}
                                                            disabled={deletingPeriodId === period.Id}
                                                            style={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: 8,
                                                                border: `1px solid ${BORDER}`,
                                                                background: deletingPeriodId === period.Id ? "rgba(0,0,0,0.04)" : "#fff",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: deletingPeriodId === period.Id ? "default" : "pointer",
                                                                color: "#b42318",
                                                                opacity: deletingPeriodId === period.Id ? 0.55 : 1,
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED }}>
                                                        Cash {this.fmt(period.TipCash ?? 0)}
                                                    </span>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED }}>
                                                        PIN {this.fmt(period.TipPin ?? 0)}
                                                    </span>
                                                    <span style={{ fontSize: 11, color: TEXT_MUTED }}>
                                                        {period.Workers.length} workers
                                                    </span>
                                                </div>
                                            </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                <WorkPeriodEditorDialog
                    open={editorMode != null}
                    title={editorMode === "edit" ? "Edit work period" : "New work period"}
                    workDayDate={normalizedWorkDay.Date}
                    period={editorPeriod}
                    onClose={() => this.setState({ editorMode: null, editorPeriod: null })}
                    onSave={async (period) => {
                        await this.persistWorkPeriod(period);
                    }}
                />
            </div>
        );
    }
}
